(() => {
  'use strict';

  let config = {};
  let configError;
  try {
    const script = document.getElementById('ufm-compare-config');
    if (script) {
      config = JSON.parse(script.textContent);
      if (!config || typeof config !== 'object' || Array.isArray(config)) {
        throw new Error('Invalid saved-lists configuration.');
      }
    }
  } catch (_) {
    config = {};
    configError = new Error('Invalid saved-lists configuration.');
  }

  const enabled = Boolean(config.endpoint);
  const loggedIn = typeof config.customer === 'string' && config.customer.trim() !== '';
  let cache = { wishlist: [], compare: [] };
  let csrfToken;
  let queue = Promise.resolve();

  function validList(list) {
    if (list !== 'wishlist' && list !== 'compare') throw new Error('Invalid saved list.');
  }

  function identity(value) {
    if (typeof value !== 'string' && !(typeof value === 'number' && Number.isSafeInteger(value))) {
      throw new Error('Invalid saved-list ID.');
    }
    const text = String(value);
    if (!/^\d+$/.test(text) || !/[1-9]/.test(text)) throw new Error('Invalid saved-list ID.');
    return text.replace(/^0+/, '');
  }

  function entry(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('Invalid saved-list entry.');
    }
    const id = identity(value.id);
    const variant = identity(value.variant);
    if (typeof value.url !== 'string' || !value.url || /[\s\\]/.test(value.url)) {
      throw new Error('Invalid saved-list product URL.');
    }
    const url = new URL(value.url, window.location.origin);
    if (url.origin !== window.location.origin || url.username || url.password ||
        !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) {
      throw new Error('Invalid saved-list product URL.');
    }
    return { id, variant, url: url.pathname + url.search + url.hash };
  }

  function entries(values) {
    if (!Array.isArray(values)) throw new Error('Invalid saved-list entries.');
    const seen = new Set();
    return values.map(entry).filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  function accept(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value) ||
        typeof value.csrfToken !== 'string' || !value.csrfToken.trim()) {
      throw new Error('Invalid saved-lists response.');
    }
    // Validate both lists before committing either list or the next CSRF token.
    const next = { wishlist: entries(value.wishlist), compare: entries(value.compare) };
    cache = next;
    csrfToken = value.csrfToken;
    window.dispatchEvent(new Event('ufm:saved-lists-updated'));
  }

  async function responseJSON(response) {
    if (response.status === 401) throw new Error('Login required to use saved lists.');
    if (!response.ok) throw new Error(`Saved-lists request failed (HTTP ${response.status}).`);
    try {
      return await response.json();
    } catch (_) {
      throw new Error('Invalid saved-lists JSON response.');
    }
  }

  const ready = Promise.resolve().then(async () => {
    if (configError) throw configError;
    if (config.endpoint !== undefined && typeof config.endpoint !== 'string') {
      throw new Error('Invalid saved-lists endpoint.');
    }
    if (!enabled) return;
    const endpoint = config.endpoint;
    const url = new URL(endpoint, window.location.origin);
    if (!/^\/apps\//.test(endpoint) || /[\s\\?#]/.test(endpoint) ||
        url.origin !== window.location.origin || !/^\/apps\//.test(url.pathname) ||
        url.pathname !== endpoint) {
      throw new Error('Invalid saved-lists endpoint.');
    }
    if (!loggedIn) return;
    accept(await responseJSON(await fetch(endpoint, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' }
    })));
  });
  // Keep the original rejecting promise available without an unhandled rejection.
  ready.catch(() => {});

  function read(list) {
    validList(list);
    return cache[list].map((item) => ({ ...item }));
  }

  async function mutate(list, action, payload = {}) {
    validList(list);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new Error('Invalid saved-list payload.');
    }
    let fields;
    let normalized;
    switch (action) {
      case 'add':
        fields = ['entry'];
        if (payload.entry && Object.keys(payload.entry).some((key) => !['id', 'variant', 'url'].includes(key))) {
          throw new Error('Invalid saved-list payload.');
        }
        normalized = { entry: entry(payload.entry) };
        break;
      case 'remove':
        fields = ['id'];
        normalized = { id: identity(payload.id) };
        break;
      case 'clear':
        fields = [];
        normalized = {};
        break;
      case 'merge':
        fields = ['entries'];
        if (Array.isArray(payload.entries) && payload.entries.some((item) =>
          !item || Object.keys(item).some((key) => !['id', 'variant', 'url'].includes(key)))) {
          throw new Error('Invalid saved-list payload.');
        }
        normalized = { entries: entries(payload.entries) };
        break;
      default:
        throw new Error('Invalid saved-list action.');
    }
    if (Object.keys(payload).some((key) => !fields.includes(key))) {
      throw new Error('Invalid saved-list payload.');
    }
    const operation = queue.then(async () => {
      await ready;
      if (!enabled) throw new Error('Saved lists are disabled.');
      if (!loggedIn) throw new Error('Login required to use saved lists.');
      accept(await responseJSON(await fetch(config.endpoint, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: {
          'Content-Type': 'application/json', Accept: 'application/json', 'X-UFM-CSRF': csrfToken
        },
        body: JSON.stringify({ list, action, ...normalized })
      })));
      return read(list);
    });
    queue = operation.catch(() => {});
    return operation;
  }

  window.UFMSavedLists = { enabled, ready, read, mutate };
})();
